// Production-only service worker registration for the CRA + Workbox build.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

let isRegistered = false;
let lastNotifiedWorkerId = null;
// Reference to the active registration, used by the update-coordination helpers.
let swRegistration = null;

const getNotifiedWorkerId = () => {
  try {
    return sessionStorage.getItem('sw_notified_worker_id');
  } catch (e) {
    return null;
  }
};

const getLastNotificationTime = () => {
  try {
    const time = sessionStorage.getItem('sw_notified_time');
    return time ? parseInt(time, 10) : 0;
  } catch (e) {
    return 0;
  }
};

const setNotifiedWorkerId = (workerId) => {
  try {
    sessionStorage.setItem('sw_notified_worker_id', workerId);
    sessionStorage.setItem('sw_notified_time', Date.now().toString());
  } catch (e) {
    // ignore
  }
};

const shouldShowNotification = (workerId) => {
  const notifiedWorkerId = getNotifiedWorkerId();
  const lastNotificationTime = getLastNotificationTime();
  const timeSinceLastNotification = Date.now() - lastNotificationTime;

  if (notifiedWorkerId === workerId && timeSinceLastNotification < 5000) {
    return false;
  }
  if (notifiedWorkerId === workerId) {
    return false;
  }
  if (lastNotifiedWorkerId === workerId) {
    return false;
  }
  return true;
};

export function register(config) {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  if (isRegistered) {
    return;
  }

  if (!('serviceWorker' in navigator)) {
    return;
  }

  isRegistered = true;
  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);

  if (publicUrl.origin !== window.location.origin && publicUrl.origin !== '') {
    return;
  }

  const publicPathPrefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const swUrl = `${publicPathPrefix}/service-worker.js`;

  if (!window.serviceWorkerLoadListenerAdded) {
    window.addEventListener('load', () => {
      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
      } else {
        registerValidSW(swUrl, config);
      }
    });
    window.serviceWorkerLoadListenerAdded = true;
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      swRegistration = registration;
      setTimeout(() => {
        if (registration.waiting && navigator.serviceWorker.controller) {
          const waitingWorkerId = registration.waiting.scriptURL;
          if (shouldShowNotification(waitingWorkerId)) {
            lastNotifiedWorkerId = waitingWorkerId;
            setNotifiedWorkerId(waitingWorkerId);
            if (config && config.onUpdate) {
              config.onUpdate(registration);
            }
          }
        }
      }, 500);

      if (!registration._updateHandlerSet) {
        registration._updateHandlerSet = true;

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker == null) {
            return;
          }

          if (installingWorker._stateChangeHandlerSet) {
            return;
          }
          installingWorker._stateChangeHandlerSet = true;

          installingWorker.onstatechange = () => {
            if (installingWorker.state !== 'installed') {
              return;
            }

            if (navigator.serviceWorker.controller) {
              const waitingWorker = registration.waiting;
              const workerId = waitingWorker
                ? waitingWorker.scriptURL
                : installingWorker.scriptURL;

              if (shouldShowNotification(workerId)) {
                lastNotifiedWorkerId = workerId;
                setNotifiedWorkerId(workerId);
                if (config && config.onUpdate) {
                  config.onUpdate(registration);
                }
              }
            } else if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          };
        };
      }
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Offline reload: skip fetch probe (it always fails) and register the existing SW.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    registerValidSW(swUrl, config);
    return;
  }

  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      console.error('Service Worker Registration: Error checking service worker:', error);
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

async function getRegistration() {
  if (swRegistration) {
    return swRegistration;
  }
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  try {
    swRegistration = (await navigator.serviceWorker.getRegistration()) || null;
  } catch (e) {
    swRegistration = null;
  }
  return swRegistration;
}

// True if a fully-downloaded new version is parked in "waiting".
export function isUpdateReady() {
  return !!(swRegistration && swRegistration.waiting);
}

// True if a service worker is controlling this page, meaning the precached shell
// can serve a reload even while offline.
export function hasOfflineShell() {
  return !!(typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.controller);
}

// Ask the browser to check for a new service worker and wait until any new
// version has finished installing (fully precached, parked in "waiting").
// Resolves: 'ready' (waiting worker present), 'none' (no new version), or 'timeout'.
export async function checkForUpdate(timeoutMs = 30000) {
  const registration = await getRegistration();
  if (!registration) {
    return 'none';
  }
  if (registration.waiting) {
    return 'ready';
  }

  try {
    await registration.update();
  } catch (e) {
    return 'none';
  }

  if (registration.waiting) {
    return 'ready';
  }

  const installing = registration.installing;
  if (!installing) {
    return 'none';
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (!settled) {
        settled = true;
        resolve(result);
      }
    };
    const timer = setTimeout(() => finish(registration.waiting ? 'ready' : 'timeout'), timeoutMs);
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed') {
        clearTimeout(timer);
        finish(registration.waiting ? 'ready' : 'timeout');
      } else if (installing.state === 'redundant') {
        clearTimeout(timer);
        finish('none');
      }
    });
  });
}

// Tell the waiting worker to take over, then reload once it controls the page.
// The reload is served entirely from the freshly precached cache, so it cannot
// fail even if the network drops at this instant.
export function activateUpdateAndReload() {
  return new Promise((resolve) => {
    const waiting = swRegistration && swRegistration.waiting;
    if (!waiting) {
      window.location.reload();
      resolve();
      return;
    }

    let reloaded = false;
    const doReload = () => {
      if (!reloaded) {
        reloaded = true;
        window.location.reload();
        resolve();
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', doReload, { once: true });
    waiting.postMessage({ type: 'SKIP_WAITING' });
    // Fallback in case controllerchange does not fire promptly.
    setTimeout(doReload, 3000);
  });
}

// Ask the service worker to confirm a fresh (non-cached) schedule.json is
// reachable and cache it. Resolves true only on a real network 200.
export function verifyFreshSchedule(timeoutMs = 8000) {
  return new Promise((resolve) => {
    const controller =
      typeof navigator !== 'undefined' && navigator.serviceWorker
        ? navigator.serviceWorker.controller
        : null;
    if (!controller) {
      resolve(false);
      return;
    }

    let settled = false;
    const finish = (val) => {
      if (!settled) {
        settled = true;
        resolve(val);
      }
    };

    const channel = new MessageChannel();
    const timer = setTimeout(() => finish(false), timeoutMs);
    channel.port1.onmessage = (event) => {
      if (event.data && event.data.type === 'FRESH_SCHEDULE_RESULT') {
        clearTimeout(timer);
        finish(!!event.data.ok);
      }
    };

    try {
      controller.postMessage({ type: 'VERIFY_FRESH_SCHEDULE' }, [channel.port2]);
    } catch (e) {
      clearTimeout(timer);
      finish(false);
    }
  });
}
